import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Search, HourglassEmpty } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

const MyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterPhone, setFilterPhone] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await api.get("/bookings");
      const today = new Date().toISOString().split("T")[0];

const todayBookings = response.data.filter(b => {
  const bookingDate = new Date(b.date).toISOString().split("T")[0];
  return bookingDate === today;
});

      setBookings(todayBookings);
    } catch (err) {
      setError("Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Format Date
  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  // ✅ Filtering
  const filteredBookings = bookings.filter((booking) => {
    const query = search.toLowerCase();
    const matchesSearch =
      booking.table?.tableNumber?.toString().includes(query) ||
      booking.status?.toLowerCase().includes(query) ||
      booking.time?.toLowerCase().includes(query) ||
      booking._id?.toLowerCase().includes(query) ||
      booking?.table?.location?.toLowerCase().includes(query) ||
      booking?.specialRequests?.toLowerCase().includes(query);

    const matchesName =
      !filterName ||
      booking?.user?.name?.toLowerCase().includes(filterName.toLowerCase());

    const matchesPhone =
      !filterPhone ||
      booking?.user?.phone?.toLowerCase().includes(filterPhone.toLowerCase());

    return matchesSearch && matchesName && matchesPhone;
  });

  // ✅ DataGrid Columns
  const columns = [
    { field: "bookingNumber", headerName: "Booking #", flex: 1 },
    { field: "name", headerName: "Customer", flex: 1 },
    { field: "phone", headerName: "Phone", flex: 1 },
    { field: "tableNumber", headerName: "Table", flex: 0.6 },
    { field: "date", headerName: "Date", flex: 1 },
    { field: "time", headerName: "Time", flex: 0.8 },
    { field: "guests", headerName: "Guests", flex: 0.6 },
    { field: "location", headerName: "Location", flex: 1 },
    { field: "status", headerName: "Status", flex: 1 },
  ];

  // ✅ DataGrid Rows
  const rows = filteredBookings.map((b) => ({
    id: b._id,
    bookingNumber: b.bookingNumber,
    name: b?.user?.name || "-",
    phone: b?.user?.phone || "-",
    tableNumber: b.table?.tableNumber || "-",
    date: formatDate(b.date),
    time: b.time,
    guests: b.numberOfGuests,
    location: b.table?.location || "-",
    status: b.status,
  }));

  // ✅ Custom CSV Export
  const handleExportCSV = () => {
    const csvHeader = columns.map((col) => col.headerName).join(",");
    const csvRows = rows.map((row) =>
      columns.map((col) => `"${row[col.field] || ""}"`).join(",")
    );
    const csvContent = [csvHeader, ...csvRows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "bookings_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 6, textAlign: "center" }}>
        <CircularProgress size={60} />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Loading your bookings...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 5 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Today’s Bookings
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="success"
            onClick={handleExportCSV}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/book-table")}
          >
            Book New Table
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box display="flex" gap={2} mb={3}>
        <TextField
          variant="outlined"
          placeholder="Search by table, status, time..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          variant="outlined"
          placeholder="Filter by Name"
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
        />
        <TextField
          variant="outlined"
          placeholder="Filter by Phone"
          value={filterPhone}
          onChange={(e) => setFilterPhone(e.target.value)}
        />
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      {rows.length === 0 ? (
        <Box textAlign="center" sx={{ mt: 5 }}>
          <HourglassEmpty sx={{ fontSize: 50, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6">No bookings found for today</Typography>
        </Box>
      ) : (
        <Box sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
              sx={{
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#1976d2 !important", // force background
      color: "#791414ff",                // header text color
      fontWeight: "bold",           // optional
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: "bold",           // makes header text bold
    }
  }}
          />
        </Box>
      )}
    </Container>
  );
};

export default MyBookings;
